import { useState } from "react";

import { Controller, FormProvider, useForm } from "react-hook-form";
import { useNavigate } from "react-router";

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { zodResolver } from "@hookform/resolvers/zod";

import { LogOutIcon } from "lucide-react";

import { AlertDialog, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Field } from "@/components/ui/field";
import { Input } from "@/components/ui/input";

import { LeaveProjectSchema, leaveProjectSchema } from "@/features/projects/schemas";
import { leaveProject } from "@/features/projects/services/project";
import { Project } from "@/features/projects/types";
import Error from "@/shared/components/Error";
import { QUERY_KEYS } from "@/shared/lib/constants/queries";
import { AppError } from "@/shared/lib/utils/errors";

interface LeaveProjectFormProps {
  project: Project;
  trigger?: React.ReactNode;
  disabled?: boolean;
}

export default function LeaveProjectForm({ project, trigger, disabled = false }: LeaveProjectFormProps) {
  const [open, setOpen] = useState(false);

  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const form = useForm<LeaveProjectSchema>({
    resolver: zodResolver(leaveProjectSchema),
    defaultValues: {
      id: project.id,
    },
  });

  const mutation = useMutation<undefined, AppError | Error, LeaveProjectSchema>({
    mutationFn: leaveProject,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_MY_PROJECTS], exact: true });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_PROJECT_BY_ID, project.id], exact: true });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_MY_PERMISSIONS_IN_PROJECT, project.id], exact: true });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_PROJECT_MEMBERS, project.id], exact: true });
      navigate("/projects");
    },
  });

  async function onSubmit(values: LeaveProjectSchema) {
    mutation.mutate(values);
  }

  if (disabled) {
    return (
      trigger ?? (
        <Button variant="ghost" size="icon-sm" className="text-muted-foreground hover:text-destructive" disabled>
          <LogOutIcon className="size-4" />
        </Button>
      )
    );
  }

  return (
    <AlertDialog
      open={open}
      onOpenChange={(newOpen) => {
        if (!mutation.isPending) {
          setOpen(newOpen);
        }
      }}
    >
      <AlertDialogTrigger asChild>
        {trigger ?? (
          <Button variant="ghost" size="icon-sm" className="text-muted-foreground hover:text-destructive">
            <LogOutIcon className="size-4" />
          </Button>
        )}
      </AlertDialogTrigger>
      <AlertDialogContent>
        <FormProvider {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-6">
            <AlertDialogHeader>
              <AlertDialogTitle>Leave &ldquo;{project.name}&rdquo;?</AlertDialogTitle>
              <AlertDialogDescription>You will immediately lose access to this project and all its tickets. An admin will need to re-invite you if you want to rejoin.</AlertDialogDescription>
            </AlertDialogHeader>
            {mutation.isError && <Error title="Unable to leave project" error={mutation.error} />}
            <Controller
              name="id"
              control={form.control}
              render={({ field, fieldState }) => (
                <Field data-invalid={fieldState.invalid}>
                  <Input type="hidden" {...field} disabled={mutation.isPending || mutation.isSuccess} aria-invalid={fieldState.invalid} />
                </Field>
              )}
            />
            <AlertDialogFooter>
              <AlertDialogCancel disabled={mutation.isPending || mutation.isSuccess}>Cancel</AlertDialogCancel>
              <Button type="submit" variant="destructive" className="disabled:opacity-40" disabled={mutation.isPending || mutation.isSuccess}>
                <LogOutIcon />
                {mutation.isPending || mutation.isSuccess ? "Leaving project..." : "Leave project"}
              </Button>
            </AlertDialogFooter>
          </form>
        </FormProvider>
      </AlertDialogContent>
    </AlertDialog>
  );
}
